import { BRAND_NAME, CONTACT_EMAIL } from "../../constants/brand";
import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Grid, Link, Typography } from '@mui/material';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import CheckIcon from '@mui/icons-material/Check';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import styles from '../../Styles/ComponentStyles/VerifyUser/VerifyUserV2.module.scss';
import newRBLogoForWhiteBg from '../../assets/Images/mar-lockup.svg';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';

const APPROVAL_BULLETS = [
    'Receive an approval confirmation email',
    'Be prompted to set up your account password',
    'Get full access to the portal',
];

const REDIRECT_SECONDS = 20;

type PageState = 'loading' | 'success' | 'error';

const VerifyUserV2 = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [pageState, setPageState] = useState<PageState>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [countdown, setCountdown] = useState<number>(REDIRECT_SECONDS);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const verify_token = searchParams.get('verify_token');

    const {
        setFlagLoader,
        verifyTokenAPI,
    } = useContext(UserContext) as UserContextPayload;

    // Start the countdown timer and redirect after REDIRECT_SECONDS
    const startCountdown = () => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
        setCountdown(REDIRECT_SECONDS);
        countdownRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current!);
                    navigate('/');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        if (verify_token) {
            handleVerifyTokenAPI();
        } else {
            navigate('/');
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });

        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, []);

    const handleVerifyTokenAPI = async () => {
        if (verify_token) {
            setFlagLoader(true);
            const response = await verifyTokenAPI(verify_token);
            setFlagLoader(false);

            if (response?.status) {
                setPageState('success');
                startCountdown();
            } else {
                setErrorMessage(response?.error?.message || 'Token verification failed. Please try again or request a new verification link.');
                setPageState('error');
                startCountdown();
            }
        }
    };

    // Countdown ring progress (SVG circle)
    const radius = 18;
    const circumference = 2 * Math.PI * radius;
    const progress = countdown / REDIRECT_SECONDS;
    const strokeDashoffset = circumference * (1 - progress);

    const CountdownTimer = () => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                mt: '4px',
            }}
        >
            {/* SVG circular countdown ring */}
            <Box sx={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
                <svg width="44" height="44" viewBox="0 0 44 44" style={{ transform: 'rotate(-90deg)' }}>
                    {/* Track */}
                    <circle
                        cx="22"
                        cy="22"
                        r={radius}
                        fill="none"
                        stroke={pageState === 'error' ? '#fde8e8' : '#e8f0fb'}
                        strokeWidth="3.5"
                    />
                    {/* Progress arc */}
                    <circle
                        cx="22"
                        cy="22"
                        r={radius}
                        fill="none"
                        stroke={pageState === 'error' ? '#e53935' : '#3B78CE'}
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        style={{ transition: 'stroke-dashoffset 0.9s linear' }}
                    />
                </svg>
                {/* Number in center */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 700,
                            color: pageState === 'error' ? '#e53935' : '#3B78CE',
                            lineHeight: 1,
                        }}
                    >
                        {countdown}
                    </Typography>
                </Box>
            </Box>

            <Typography
                sx={{
                    fontSize: '14px',
                    color: pageState === 'error' ? '#9b1c1c' : '#4a5568',
                    lineHeight: 1.5,
                }}
            >
                Redirecting to home page in{' '}
                <strong style={{ color: pageState === 'error' ? '#e53935' : '#3B78CE' }}>
                    {countdown} second{countdown !== 1 ? 's' : ''}
                </strong>
                .{' '}
                <Link
                    component="button"
                    onClick={() => {
                        if (countdownRef.current) clearInterval(countdownRef.current);
                        navigate('/');
                    }}
                    sx={{
                        fontSize: '13px',
                        color: pageState === 'error' ? '#e53935' : '#3B78CE',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                    }}
                    underline="always"
                >
                    Go now
                </Link>
            </Typography>
        </Box>
    );

    // ── Error State ────────────────────────────────────────────────
    if (pageState === 'error') {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                className={styles.pageWrapper}
            >
                <Grid item xs={12} sm={10} md={6} lg={5} xl={5}>
                    <Box
                        component="img"
                        alt={BRAND_NAME}
                        src={newRBLogoForWhiteBg}
                        height="30px"
                        sx={{ display: 'block', mx: 'auto', mb: '30px' }}
                    />
                    <Box className={styles.card}>
                        {/* Header */}
                        <Box
                            className={styles.header}
                            sx={{ borderBottom: '1px solid #fde8e8 !important' }}
                        >
                            <Grid container direction="column" alignItems="center" gap={0}>
                                <Grid item>
                                    <Box
                                        className={styles.headerIconCircle}
                                        sx={{ backgroundColor: '#fde8e8 !important' }}
                                    >
                                        <CancelOutlinedIcon sx={{ color: '#e53935', fontSize: 40 }} />
                                    </Box>
                                </Grid>
                                <Grid item>
                                    <Typography
                                        className={styles.headerTitle}
                                        sx={{ color: '#ffff !important' }}
                                    >
                                        Verification Failed
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography className={styles.headerSubtitle}>
                                        We were unable to verify your email address. Please see the details below.
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Body */}
                        <Box className={styles.body}>
                            <Grid container direction="column" gap={2.5}>

                                {/* Error detail box */}
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#fff5f5',
                                            border: '1px solid #feb2b2',
                                            borderRadius: '10px',
                                            padding: '16px 18px',
                                        }}
                                    >
                                        <Grid container gap={1.5} alignItems="flex-start" wrap="nowrap">
                                            <Grid item>
                                                <Box
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#e53935',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <ErrorOutlineOutlinedIcon sx={{ color: '#fff', fontSize: 20 }} />
                                                </Box>
                                            </Grid>
                                            <Grid item xs>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 700,
                                                        color: '#b91c1c',
                                                        mb: '4px',
                                                    }}
                                                >
                                                    Error Details
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '13.5px',
                                                        color: '#7f1d1d',
                                                        lineHeight: 1.6,
                                                    }}
                                                >
                                                    {errorMessage}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>

                                {/* What to do next */}
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#fafafa',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '10px',
                                            padding: '16px 18px',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '13.5px',
                                                fontWeight: 700,
                                                color: '#2d3748',
                                                mb: '8px',
                                            }}
                                        >
                                            What can you do?
                                        </Typography>
                                        {[
                                            'Check if the verification link has expired and request a new one.',
                                            'Ensure you are using the most recent verification email.',
                                            'Contact support if the problem persists.',
                                        ].map((line) => (
                                            <Box
                                                key={line}
                                                sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start', mb: '6px' }}
                                            >
                                                <span style={{ color: '#e53935', fontWeight: 700, fontSize: '14px', lineHeight: 1.6 }}>→</span>
                                                <Typography sx={{ fontSize: '14px', color: 'black', lineHeight: 1.6 }}>
                                                    {line}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>

                                {/* Countdown */}
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#fff5f5',
                                            border: '1px solid #feb2b2',
                                            borderRadius: '10px',
                                            padding: '12px 18px',
                                        }}
                                    >
                                        <CountdownTimer />
                                    </Box>
                                </Grid>

                                {/* Footer */}
                                <Grid item xs={12}>
                                    <Typography className={styles.footerText}>
                                        Need help? Contact our support team at{' '}
                                        <Link href={`mailto:${CONTACT_EMAIL}`} className={styles.footerEmail}>
                                            {CONTACT_EMAIL}
                                        </Link>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        );
    }

    // ── Loading: API in flight (no success copy yet) ─────────────────
    if (pageState === 'loading') {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                className={styles.pageWrapper}
            >
                <Grid item xs={12} sm={10} md={6} lg={5} xl={5}>
                    <Box
                        component="img"
                        alt={BRAND_NAME}
                        src={newRBLogoForWhiteBg}
                        height="30px"
                        sx={{ display: 'block', mx: 'auto', mb: '30px' }}
                    />
                    <Box className={styles.card}>
                        <Box className={styles.header}>
                            <Grid container direction="column" alignItems="center" gap={2}>
                                <Grid item>
                                    <Typography className={styles.headerTitle}>
                                        Verifying your email
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography className={styles.headerSubtitle}>
                                        Please wait while we confirm your verification link.
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        );
    }

    // ── Success: shown only after verify API succeeds; always includes 20s redirect timer ──
    return (
        <Grid
            container
            justifyContent="center"
            alignItems="center"
            className={styles.pageWrapper}
        >
            <Grid item xs={12} sm={10} md={6} lg={5} xl={5}>
                <Box
                    component="img"
                    alt={BRAND_NAME}
                    src={newRBLogoForWhiteBg}
                    height="30px"
                    sx={{ display: 'block', mx: 'auto', mb: '30px' }}
                />
                <Box className={styles.card}>
                    <Box className={styles.header}>
                        <Grid container direction="column" alignItems="center" gap={0}>
                            <Grid item>
                                <Box className={styles.headerIconCircle}>
                                    <MarkunreadOutlinedIcon sx={{ color: '#3B78CE', fontSize: 40 }} />
                                </Box>
                            </Grid>
                            <Grid item>
                                <Typography className={styles.headerTitle}>
                                    Email Verification Complete!
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography className={styles.headerSubtitle}>
                                    Your email has been verified successfully. Your account is now pending
                                    admin approval.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box className={styles.body}>
                        <Grid container direction="column" gap={2.5}>

                            {/* Email verified step */}
                            <Grid item xs={12}>
                                <Box className={`${styles.statusRow} ${styles.emailVerifiedRow}`}>
                                    <Grid container gap={2} alignItems="flex-start" wrap="nowrap">
                                        <Grid item>
                                            <Box className={`${styles.circularIcon} ${styles.iconSuccess}`}>
                                                <CheckIcon sx={{ color: '#ffffff', fontSize: 26 }} />
                                            </Box>
                                        </Grid>
                                        <Grid item xs>
                                            <Grid container alignItems="center" gap={1} sx={{ mb: '4px' }} flexWrap="wrap">
                                                <Grid item>
                                                    <Typography className={styles.stepTitle}>
                                                        Email Verified
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <span className={styles.badgeCompleted}>Completed</span>
                                                </Grid>
                                            </Grid>
                                            <Typography className={styles.stepDescription}>
                                                Your email address has been successfully verified. Great job!
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Admin approval step */}
                            <Grid item xs={12}>
                                <Box className={`${styles.statusRow} ${styles.adminPendingRow}`}>
                                    <Grid container gap={2} alignItems="flex-start" wrap="nowrap">
                                        <Grid item>
                                            <Box className={`${styles.circularIcon} ${styles.iconPending}`}>
                                                <GppGoodOutlinedIcon sx={{ color: '#ffffff', fontSize: 22 }} />
                                            </Box>
                                        </Grid>
                                        <Grid item xs>
                                            <Grid container alignItems="center" gap={1} sx={{ mb: '4px' }} flexWrap="wrap">
                                                <Grid item>
                                                    <Typography className={styles.stepTitle}>
                                                        Waiting for Admin Approval
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <span className={styles.badgeInReview}>In Review</span>
                                                </Grid>
                                            </Grid>
                                            <Typography className={styles.stepDescription}>
                                                Our admin team has been notified and will review your registration
                                                request shortly. You&apos;ll receive an email notification once
                                                approved.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Important info */}
                            <Grid item xs={12}>
                                <Box className={styles.importantBox}>
                                    <Grid container gap={1.5} alignItems="flex-start" wrap="nowrap">
                                        <Grid item>
                                            <Box className={`${styles.circularIcon} ${styles.iconWarning}`}>
                                                <ErrorOutlineOutlinedIcon sx={{ color: '#ffffff', fontSize: 22 }} />
                                            </Box>
                                        </Grid>
                                        <Grid item xs>
                                            <Typography className={styles.importantTitle}>
                                                Important Information
                                            </Typography>
                                            <Typography className={styles.importantIntro}>
                                                Our admin team typically reviews new registrations within 24-48
                                                hours. Once approved, you will:
                                            </Typography>
                                            <Box>
                                                {APPROVAL_BULLETS.map((line) => (
                                                    <Box key={line} className={styles.importantBullet}>
                                                        <span className={styles.importantBulletArrow}>→</span>
                                                        <Typography className={styles.importantBulletText}>
                                                            {line}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* 20s redirect — same behavior as error state; runs after successful verify API */}
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        backgroundColor: '#f0f6ff',
                                        border: '1px solid #bfd7f5',
                                        borderRadius: '10px',
                                        padding: '12px 18px',
                                    }}
                                >
                                    <CountdownTimer />
                                </Box>
                            </Grid>

                            {/* Footer */}
                            <Grid item xs={12}>
                                <Typography className={styles.footerText}>
                                    Need help? Contact our support team at{' '}
                                    <Link
                                        href={`mailto:${CONTACT_EMAIL}`}
                                        className={styles.footerEmail}
                                    >
                                        {CONTACT_EMAIL}
                                    </Link>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
};

export default VerifyUserV2;